/**
 * The watchlist pitch as a PDF (PV19, SE-8361 / PRD-7.6 "Pitch: PDF of the
 * field").
 *
 * `toPitchPdfModel` projects the complete Pitch read through the SAME
 * functions the screen renders from (`projectPitchMembers`, the controller's
 * ranks and slots, `groupPitchMembers`, `projectSquadOverlay`, the card's
 * average), so the file cannot disagree with the field. `renderWatchlistPitchPdf`
 * draws that model as a vector page — markings, one card per slot on the
 * formation's row grid, ● squad / ○ target rows, "Avg. GPR n", "No targets",
 * "+ N more" — and a "Players by position" table on the pages after it that
 * lists every member 1..N and the Unassigned bench, so a large list is
 * explicit rather than clipped to three names. Rasterising the DOM is
 * deliberately not done: it would bake the viewport and hover state into
 * the file.
 * @module features/scouting/utils/watchlistPitchPdf
 */

import { weakestSlotSetFromSquad } from "@/features/scouting/utils/threeWeakest";
import { PITCH_FORMATION_ROWS } from "@/features/scouting/constants/pitchFormationRows";
import type { WatchlistPreferenceOrderController } from "@/features/scouting/hooks/useWatchlistPreferenceOrder";
import { orderItemsByPreference } from "@/features/scouting/hooks/watchlistPreferenceOrderState";
import {
  projectSquadOverlay,
  type SquadOverlayPlayer,
} from "@/features/scouting/utils/watchlistPitchOverlay";
import {
  formatPitchFormation,
  formatPitchGpr,
} from "@/features/scouting/utils/watchlistPitchPresentation";
import type {
  PitchFormation,
  ScoutingGetWatchlistPitchQuery,
  TacticalSlot,
} from "@/generated/graphql";

/** Authorized Pitch payload. */
type Pitch = NonNullable<ScoutingGetWatchlistPitchQuery["getWatchlistPitch"]>;
/** One watchlist member as the Pitch read returns it. */
type Member = Pitch["members"]["edges"][number]["node"];

/** The complete Pitch read, members flattened (every page). */
export interface PitchPdfSource {
  readonly name: string;
  readonly formation: string;
  readonly pitchRevision: number;
  readonly watchlistCount: number;
  readonly members: readonly Member[];
  readonly overlay: readonly SquadOverlayPlayer[];
}

/** What the screen adds to the read: the switch and the live controller. */
export interface PitchPdfOptions {
  readonly showSquadOnPitch: boolean;
  readonly preferenceOrder?: Pick<
    WatchlistPreferenceOrderController,
    "orderedPlayerIds" | "rankFor"
  > & {
    readonly slotFor: (playerId: string) => string | null | undefined;
    readonly formation?: string;
  };
}

/** One list member on a card or in the table. */
export interface PitchPdfTarget {
  readonly rank: number;
  readonly name: string;
  /** Natural positions, comma-joined. */
  readonly positions: string;
  /** GPR to one decimal, or the em dash. */
  readonly gpr: string;
  /** GPR as the card prints it (whole number) — kept for the badge only. */
  readonly gprRaw: number | null;
}

/** One squad-overlay row on a card. */
export interface PitchPdfSquadRow {
  readonly name: string;
  readonly gpr: string;
  /** Natural positions, comma-joined, for the table (SE-8526). */
  readonly positions?: string;
}

/** One slot card as the field shows it. */
export interface PitchPdfCard {
  readonly slot: TacticalSlot;
  /** "Avg. GPR" badge value: integer average over targets + squad rows. */
  readonly averageGpr: string;
  readonly squad: readonly PitchPdfSquadRow[];
  /** Every member in rank order. */
  readonly targets: readonly PitchPdfTarget[];
  /** The three the card shows. */
  readonly topTargets: readonly PitchPdfTarget[];
  /** "+ N more" when the card shows "Show all N". */
  readonly moreCount: number;
  /** "No targets": nothing to draw. */
  readonly empty: boolean;
  /** One of the three weakest positions: the screen's dashed border (SE-8526). */
  readonly dashed: boolean;
}

/** The model both the field page and the table read from. */
export interface PitchPdfModel {
  readonly listName: string;
  readonly formation: string;
  readonly formationLabel: string;
  readonly rows: readonly (readonly TacticalSlot[])[];
  readonly cards: Partial<Record<TacticalSlot, PitchPdfCard>>;
  /** Members whose slot the formation does not render (never on the field). */
  readonly offFieldSlots: Partial<Record<string, readonly PitchPdfTarget[]>>;
  readonly unassigned: readonly PitchPdfTarget[];
  readonly memberCount: number;
  readonly overlayOn: boolean;
  readonly pitchRevision: number;
  /** "{list} · {formation} · {N players}{ · squad overlay on}" */
  readonly title: string;
}

/** Cards show this many names before "Show all N". */
const CARD_TOP_COUNT = 3;

/**
 * GPR to one decimal for the table, or the em dash.
 * @param gpr - Member GPR
 * @returns The text.
 */
const tableGpr = (gpr: number | null | undefined): string =>
  typeof gpr === "number" ? gpr.toFixed(1) : "—";

/**
 * The card's badge: integer average over the rated players, as the screen.
 * @param ratings - GPR values, unrated ones excluded
 * @returns Formatted average or the em dash.
 */
const averageBadge = (ratings: readonly number[]): string =>
  ratings.length > 0
    ? formatPitchGpr(
        ratings.reduce((total, rating) => total + rating, 0) / ratings.length
      )
    : "—";

/**
 * A member as the table and the cards print them.
 * @param member - The projected member
 * @param rank - Its rank on screen
 * @returns The target row.
 */
const toTarget = (member: Member, rank: number): PitchPdfTarget => ({
  rank,
  name: member.displayName,
  positions:
    member.naturalPositions
      .map(position => position.abbreviation)
      .filter((code): code is string => Boolean(code))
      .join(", ") || "—",
  gpr: tableGpr(member.gpr),
  gprRaw: typeof member.gpr === "number" ? member.gpr : null,
});

/**
 * One slot card from its members and overlay rows.
 * @param slot - The slot
 * @param targets - Members in rank order
 * @param squad - Squad-overlay players placed here (empty while off)
 * @param dashed - One of the three weakest positions (SE-8526)
 * @returns The card.
 */
const toCard = (
  slot: TacticalSlot,
  targets: readonly PitchPdfTarget[],
  squad: readonly SquadOverlayPlayer[],
  dashed: boolean
): PitchPdfCard => ({
  slot,
  averageGpr: averageBadge([
    ...targets.flatMap(target =>
      target.gprRaw === null ? [] : [target.gprRaw]
    ),
    ...squad.flatMap(player =>
      typeof player.gpr === "number" ? [player.gpr] : []
    ),
  ]),
  squad: squad.map(player => ({
    name: player.displayName,
    gpr: formatPitchGpr(player.gpr),
    positions:
      player.naturalPositions
        .map(position => position.abbreviation)
        .filter((code): code is string => Boolean(code))
        .join(", ") || "—",
  })),
  targets,
  topTargets: targets.slice(0, CARD_TOP_COUNT),
  moreCount: Math.max(0, targets.length - CARD_TOP_COUNT),
  empty: targets.length === 0 && squad.length === 0,
  dashed,
});

/**
 * Builds the PDF model from the complete read and the screen's state.
 * @param source - The complete Pitch read
 * @param options - The overlay switch and the live order controller
 * @returns The model.
 */
export const toPitchPdfModel = (
  source: PitchPdfSource,
  options: PitchPdfOptions
): PitchPdfModel => {
  const { preferenceOrder, showSquadOnPitch } = options;
  const formation = preferenceOrder?.formation ?? source.formation;
  const rows = PITCH_FORMATION_ROWS[formation as PitchFormation] ?? [];
  const slots = rows.flat();
  const rendered: ReadonlySet<string> = new Set(slots);
  // The screen's projection: controller order, controller slots, ranks.
  const members = orderItemsByPreference(
    source.members,
    preferenceOrder?.orderedPlayerIds ?? [],
    member => member.playerId
  ).map(member => {
    const slot = preferenceOrder?.slotFor(member.playerId);
    return {
      ...member,
      effectiveSlot:
        slot === undefined
          ? member.effectiveSlot
          : (slot as TacticalSlot | null),
      rank: preferenceOrder?.rankFor(member.playerId) ?? member.rank,
    };
  });
  // Projected whatever the switch says: the weakest slots read it (SE-8669).
  // Overlap is judged against the complete membership, as on the screen: a
  // filtered export reads only the members its filter matches.
  const completeIds = preferenceOrder?.orderedPlayerIds;
  const squadBySlot = projectSquadOverlay(
    source.overlay,
    new Set(
      completeIds && completeIds.length > 0
        ? completeIds
        : members.map(member => member.playerId)
    ),
    slots
  ).squadBySlot;
  const overlay = showSquadOnPitch ? squadBySlot : {};
  const bySlot = members.reduce<Record<string, PitchPdfTarget[]>>(
    (groups, member) => {
      const key = member.effectiveSlot ?? "";
      return {
        ...groups,
        [key]: [...(groups[key] ?? []), toTarget(member, member.rank)],
      };
    },
    {}
  );
  // SE-8526: the same three weakest slots the screen dashes, read from the
  // cards' own membership (SE-8669).
  const weakest = weakestSlotSetFromSquad(slots, squadBySlot);
  const cards = Object.fromEntries(
    slots.map(slot => [
      slot,
      toCard(slot, bySlot[slot] ?? [], overlay[slot] ?? [], weakest.has(slot)),
    ])
  ) as Partial<Record<TacticalSlot, PitchPdfCard>>;
  const offFieldSlots = Object.fromEntries(
    Object.entries(bySlot).filter(([key]) => key !== "" && !rendered.has(key))
  );
  const formationLabel = formatPitchFormation(formation);
  return {
    listName: source.name,
    formation,
    formationLabel,
    rows,
    cards,
    offFieldSlots,
    unassigned: bySlot[""] ?? [],
    memberCount: members.length,
    overlayOn: showSquadOnPitch,
    pitchRevision: source.pitchRevision,
    title: `${source.name} · ${formationLabel} · ${members.length} players${
      showSquadOnPitch ? " · squad overlay on" : ""
    }`,
  };
};

/** The part of a jsPDF document the field renderer touches. */
export interface PitchPdfDocument {
  text: (
    text: string,
    x: number,
    y: number,
    options?: { align?: "left" | "right" | "center" }
  ) => unknown;
  setFontSize: (size: number) => unknown;
  setFont: (family: string, style: "normal" | "bold") => unknown;
  setDrawColor: (r: number, g: number, b: number) => unknown;
  setFillColor: (r: number, g: number, b: number) => unknown;
  setLineWidth: (width: number) => unknown;
  setLineDashPattern: (pattern: number[], phase: number) => unknown;
  rect: (x: number, y: number, w: number, h: number, style?: string) => unknown;
  line: (x1: number, y1: number, x2: number, y2: number) => unknown;
  circle: (x: number, y: number, r: number, style?: string) => unknown;
  addPage: () => unknown;
  setPage: (page: number) => unknown;
  getNumberOfPages: () => number;
  internal: { pageSize: { getWidth: () => number; getHeight: () => number } };
  save: (filename: string) => unknown;
}

/** A jsPDF constructor as the lazy module exposes it. */
type JsPdfConstructor = new (options: {
  orientation: "portrait" | "landscape";
  unit: "pt";
  format: "a4";
}) => PitchPdfDocument;
/** The autotable plugin function as the lazy module exposes it. */
export type AutoTable = (
  doc: unknown,
  options: Record<string, unknown>
) => unknown;

/** Module loaders, injectable so tests never load the real libraries. */
export interface PitchPdfLoaders {
  readonly loadJsPdf: () => Promise<{ jsPDF: JsPdfConstructor }>;
  readonly loadAutoTable: () => Promise<AutoTable>;
}

const DEFAULT_LOADERS: PitchPdfLoaders = {
  loadJsPdf: async () =>
    (await import("jspdf")) as unknown as { jsPDF: JsPdfConstructor },
  loadAutoTable: async () =>
    (await import("jspdf-autotable")).default as unknown as AutoTable,
};

/** Footer inputs. */
export interface PitchPdfMeta {
  /** yyyy-MM-dd of the export. */
  readonly exportedAt: string;
  /**
   * PV20 (SE-8362): pages a caller appends after the player table and before
   * the page numbers — the scenario's KPI page. Receives the autotable
   * function so an appended page can carry a table of its own.
   */
  readonly appendPages?: (pdf: PitchPdfDocument, autoTable: AutoTable) => void;
}

/**
 * Characters jsPDF's standard fonts can draw (WinAnsiEncoding): ASCII,
 * Latin-1 and the cp1252 extras. Anything else prints as garbage — "Đorđe
 * Petrović" came out as spaced, broken glyphs (SE-8526).
 */
const WIN_ANSI_EXTRAS = new Set("€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ".split(""));

/** Letters NFD cannot decompose into a base letter. */
const TRANSLITERATE: Readonly<Record<string, string>> = {
  Đ: "D",
  đ: "d",
  Ł: "L",
  ł: "l",
  ı: "i",
  Ħ: "H",
  ħ: "h",
};

/**
 * Makes text drawable by the standard PDF font: keeps what WinAnsi covers
 * (é, ü, ñ stay), transliterates the rest to its base letter (ć → c,
 * đ → d) and drops what has none.
 * @param text - Any text
 * @returns Text every glyph of which the font can draw.
 */
export const toWinAnsi = (text: string): string =>
  Array.from(text)
    .map(char => {
      const code = char.codePointAt(0) ?? 0;
      if (code < 0x100 || WIN_ANSI_EXTRAS.has(char)) return char;
      if (TRANSLITERATE[char]) return TRANSLITERATE[char];
      const base = char.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      return base.length > 0 && (base.codePointAt(0) ?? 0) < 0x100 ? base : "";
    })
    .join("");

/* Page geometry in points (landscape A4 = 842 × 595). */
const MARGIN = 32;
const TITLE_Y = 34;
const FIELD_TOP = 52;
const FIELD_BOTTOM_GAP = 44;
const CARD_WIDTH = 132;
const CARD_HEIGHT = 74;
const CARD_PAD = 5;
const LINE = 10;
const FONT_TITLE = 12;
const FONT_CARD = 7.5;
const FONT_FOOT = 8;
/** An RGB triple. */
type Rgb = readonly [number, number, number];
const INK: Rgb = [17, 24, 39];
const LINE_COLOR: Rgb = [156, 163, 175];
const FIELD_FILL: Rgb = [243, 244, 246];
const CARD_FILL: Rgb = [255, 255, 255];
const TARGET_INK: Rgb = [10, 132, 255];

/** A rectangle in page points. */
interface Box {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** One text line of a card. */
interface CardLine {
  readonly text: string;
  readonly bold: boolean;
  readonly marker: "squad" | "target" | null;
}

/**
 * Player lines a card prints before "+ N more". The card is a fixed
 * CARD_HEIGHT: the head plus five lines fit, so four players and the "more"
 * line (SE-8526 — a squad of seven at LW ran out of the card).
 */
const PDF_CARD_LINE_CAP = 4;

/**
 * The lines a card prints: "No targets", or up to four player lines — ●
 * squad first, then ○ targets, the slot's first line bold as on screen
 * (rank=first, Figma 1010:20293) — and "+ N more" for the rest.
 * @param card - The card model
 * @returns The lines in order.
 */
const cardLines = (card: PitchPdfCard): readonly CardLine[] => {
  if (card.empty) return [{ text: "No targets", bold: false, marker: null }];
  const players: readonly CardLine[] = [
    ...card.squad.map(player => ({
      text: `${player.name}  ${player.gpr}`,
      bold: false,
      marker: "squad" as const,
    })),
    ...card.targets.map(target => ({
      text: `${target.name}  ${formatPitchGpr(target.gprRaw)}`,
      bold: false,
      marker: "target" as const,
    })),
  ];
  const shown = players
    .slice(0, PDF_CARD_LINE_CAP)
    .map((line, index) => (index === 0 ? { ...line, bold: true } : line));
  const more = players.length - shown.length;
  return more > 0
    ? [...shown, { text: `+ ${more} more`, bold: false, marker: null }]
    : shown;
};

/**
 * Draws the field markings inside the given box.
 * @param pdf - The document
 * @param box - Field box
 */
const drawMarkings = (pdf: PitchPdfDocument, box: Box): void => {
  const midY = box.y + box.h / 2;
  const boxW = box.w * 0.5;
  const boxH = box.h * 0.16;
  const boxX = box.x + (box.w - boxW) / 2;
  const radius = Math.min(box.w, box.h) * 0.12;
  pdf.setFillColor(...FIELD_FILL);
  pdf.setDrawColor(...LINE_COLOR);
  pdf.setLineWidth(1);
  pdf.rect(box.x, box.y, box.w, box.h, "FD");
  pdf.line(box.x, midY, box.x + box.w, midY);
  pdf.circle(box.x + box.w / 2, midY, radius, "S");
  pdf.rect(boxX, box.y, boxW, boxH, "S");
  pdf.rect(boxX, box.y + box.h - boxH, boxW, boxH, "S");
};

/**
 * Draws one card line with its ● / ○ marker.
 * @param pdf - The document
 * @param entry - The line
 * @param x - Left of the card
 * @param ly - Baseline of the line
 */
const drawCardLine = (
  pdf: PitchPdfDocument,
  entry: CardLine,
  x: number,
  ly: number
): void => {
  // SE-8526: the screen's markers — ● squad in accent, ○ target as a ring.
  const ink = entry.marker === "squad" ? TARGET_INK : LINE_COLOR;
  if (entry.marker) {
    pdf.setFillColor(...ink);
    pdf.setDrawColor(...ink);
    pdf.circle(
      x + CARD_PAD + 2,
      ly - 2,
      1.8,
      entry.marker === "squad" ? "F" : "S"
    );
  }
  pdf.setFont("helvetica", entry.bold ? "bold" : "normal");
  pdf.text(toWinAnsi(entry.text), x + CARD_PAD + 8, ly);
};

/**
 * Draws one slot card centred at (cx, cy).
 * @param pdf - The document
 * @param card - The card model
 * @param cx - Centre x
 * @param cy - Centre y
 */
const drawCard = (
  pdf: PitchPdfDocument,
  card: PitchPdfCard,
  cx: number,
  cy: number
): void => {
  const x = cx - CARD_WIDTH / 2;
  const y = cy - CARD_HEIGHT / 2;
  const headY = y + CARD_PAD + LINE - 3;
  const lines = cardLines(card);
  pdf.setFillColor(...CARD_FILL);
  pdf.setDrawColor(...LINE_COLOR);
  pdf.setLineWidth(card.dashed ? 1 : 0.6);
  if (card.dashed) pdf.setLineDashPattern([2, 2], 0);
  pdf.rect(x, y, CARD_WIDTH, CARD_HEIGHT, "FD");
  if (card.dashed) pdf.setLineDashPattern([], 0);
  pdf.setFontSize(FONT_CARD);
  pdf.setFont("helvetica", "bold");
  pdf.setDrawColor(...INK);
  pdf.text(card.slot, x + CARD_PAD, headY);
  pdf.setFont("helvetica", "normal");
  pdf.text(`Avg. GPR ${card.averageGpr}`, x + CARD_WIDTH - CARD_PAD, headY, {
    align: "right",
  });
  lines.forEach((entry, index) =>
    drawCardLine(pdf, entry, x, y + CARD_PAD + LINE * (index + 2) - 3)
  );
  pdf.setFont("helvetica", "normal");
};

/**
 * Draws every slot card on the formation's row grid inside the field.
 * @param pdf - The document
 * @param model - The model
 * @param field - Field box
 */
const drawCards = (
  pdf: PitchPdfDocument,
  model: PitchPdfModel,
  field: Box
): void => {
  // Attack row first, goalkeeper last — the screen's row grid.
  const rowStep = field.h / model.rows.length;
  model.rows.forEach((row, rowIndex) => {
    const cy = field.y + rowStep * (rowIndex + 0.5);
    const colStep = field.w / row.length;
    row.forEach((slot, colIndex) => {
      const card = model.cards[slot];
      if (card) drawCard(pdf, card, field.x + colStep * (colIndex + 0.5), cy);
    });
  });
};

/**
 * Writes "Page n of N" at the bottom right of every page.
 * @param pdf - The document
 */
const numberPages = (pdf: PitchPdfDocument): void => {
  const pageCount = pdf.getNumberOfPages();
  const x = pdf.internal.pageSize.getWidth() - MARGIN;
  const y = pdf.internal.pageSize.getHeight() - 14;
  const pages = Array.from({ length: pageCount }, (_, index) => index + 1);
  pdf.setFontSize(FONT_FOOT);
  pages.forEach(page => {
    pdf.setPage(page);
    pdf.text(`Page ${page} of ${pageCount}`, x, y, { align: "right" });
  });
};

/**
 * The "Players by position" table body: every slot in field order, every
 * member 1..N, then the slots the formation does not render, then Unassigned.
 * @param model - The model
 * @returns The autotable body rows.
 */
const tableBody = (model: PitchPdfModel): readonly (readonly string[])[] => {
  const rowsFor = (label: string, targets: readonly PitchPdfTarget[]) =>
    targets.map(target => [
      label,
      "Target",
      String(target.rank),
      target.name,
      target.positions,
      target.gpr,
    ]);
  // SE-8526: the ● squad the field draws is listed too — a scenario with no
  // added players printed an empty table under a full pitch.
  const squadRowsFor = (slot: string, card: PitchPdfCard | undefined) =>
    model.overlayOn
      ? (card?.squad ?? []).map(player => [
          slot,
          "Squad",
          "—",
          player.name,
          player.positions ?? "—",
          player.gpr,
        ])
      : [];
  return [
    ...model.rows
      .flat()
      .flatMap(slot => [
        ...squadRowsFor(slot, model.cards[slot]),
        ...rowsFor(slot, model.cards[slot]?.targets ?? []),
      ]),
    ...Object.entries(model.offFieldSlots).flatMap(([slot, targets]) =>
      rowsFor(slot, targets ?? [])
    ),
    ...rowsFor("Unassigned", model.unassigned),
  ];
};

/**
 * Renders the model into a jsPDF document. The caller saves it.
 * @param model - From `toPitchPdfModel`
 * @param meta - Footer inputs
 * @param loaders - Module loaders (tests inject fakes)
 * @returns The rendered document.
 */
export const renderWatchlistPitchPdf = async (
  model: PitchPdfModel,
  meta: PitchPdfMeta,
  loaders: PitchPdfLoaders = DEFAULT_LOADERS
): Promise<PitchPdfDocument> => {
  const [{ jsPDF }, autoTable] = await Promise.all([
    loaders.loadJsPdf(),
    loaders.loadAutoTable(),
  ]);
  const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const width = pdf.internal.pageSize.getWidth();
  const height = pdf.internal.pageSize.getHeight();
  const field: Box = {
    x: MARGIN,
    y: FIELD_TOP,
    w: width - MARGIN * 2,
    h: height - FIELD_TOP - FIELD_BOTTOM_GAP,
  };
  const legendY = field.y + field.h + 14;
  const footer = `Exported ${meta.exportedAt} · pitch revision ${model.pitchRevision}`;
  const body = tableBody(model).map(row => row.map(toWinAnsi));
  pdf.setFontSize(FONT_TITLE);
  pdf.setFont("helvetica", "bold");
  pdf.text(toWinAnsi(model.title), MARGIN, TITLE_Y);
  pdf.setFont("helvetica", "normal");
  drawMarkings(pdf, field);
  drawCards(pdf, model, field);
  pdf.setFontSize(FONT_FOOT);
  pdf.setDrawColor(...INK);
  // The standard font has no ● / ○: draw them, as the card lines do.
  pdf.setDrawColor(...LINE_COLOR);
  pdf.circle(MARGIN + 2, legendY - 2.5, 2, "S");
  pdf.text("Target", MARGIN + 7, legendY);
  if (model.overlayOn) {
    pdf.setFillColor(...TARGET_INK);
    pdf.setDrawColor(...TARGET_INK);
    pdf.circle(MARGIN + 62, legendY - 2.5, 2, "F");
    pdf.text("Squad", MARGIN + 67, legendY);
  }
  pdf.text(`Unassigned · ${model.unassigned.length}`, MARGIN + 130, legendY);
  pdf.text(footer, width - MARGIN, legendY, { align: "right" });
  pdf.addPage();
  pdf.setFontSize(FONT_TITLE);
  pdf.text("Players by position", MARGIN, TITLE_Y);
  autoTable(pdf, {
    startY: FIELD_TOP,
    head: [["Position", "Source", "Rank", "Player", "Positions", "GPR"]],
    body: body.map(row => [...row]),
    showHead: "everyPage",
    styles: { fontSize: 8, overflow: "ellipsize" },
    margin: { top: FIELD_TOP, bottom: MARGIN, left: MARGIN, right: MARGIN },
  });
  meta.appendPages?.(pdf, autoTable);
  numberPages(pdf);
  return pdf;
};
